import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { capitalizeEachWord } from '@ltvco/refresh-lib/utils';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface FeatureGatedRouteProps {
  children: JSX.Element;
  featureKey?: string;
  reportTypeToFeature?: boolean;
}

export function FeatureGatedRoute({
  children,
  featureKey,
  reportTypeToFeature = false,
}: FeatureGatedRouteProps) {
  const { getAccessType } = useSubscriptionAccessConfigContext();
  const navigate = useNavigate();
  const params = useParams();
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  const [searchType, setSearchType] = useState('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);

  // redirects to dashboard for blocked features LC 09/03/2025
  const handleModalClose = () => {
    closeFeatureBlockedModal();
    navigate('/dashboard');
  };

  // maps report types to feature keys LC 09/03/2025
  const REPORT_TYPE_TO_FEATURE: Record<string, string> = {
    person: 'person',
    phone: 'phone',
    email: 'email',
    property: 'property',
    vehicle: 'vehicle',
    username: 'username',
    contact: 'contact',
    obituary: 'obituary',
    ancestor: 'ancestry',
    'phone-fraud': 'fraud_scan',
    'email-fraud': 'fraud_scan',
    'ip-fraud': 'fraud_scan',
    'url-fraud': 'fraud_scan',
  };

  // determines the actual feature to check LC 09/03/2025
  const actualFeatureKey =
    reportTypeToFeature && params.reportType
      ? REPORT_TYPE_TO_FEATURE[params.reportType]
      : featureKey;

  // Get current access type for rendering decisions LC 09/03/2025
  const currentAccessType = actualFeatureKey
    ? getAccessType(actualFeatureKey)
    : 'view';

  useEffect(() => {
    if (!actualFeatureKey) return;

    if (currentAccessType === 'hide') {
      // Hidden features: redirect to dashboard LC 09/03/2025
      navigate('/dashboard');
    } else if (currentAccessType === 'block') {
      // Blocked features: show upsell modal LC 09/03/2025
      setSearchType(capitalizeEachWord(actualFeatureKey.replace('_', ' ')));
      openFeatureBlockedModal();
    }
  }, [
    actualFeatureKey,
    currentAccessType,
    navigate,
    openFeatureBlockedModal,
    setSearchType,
  ]);

  // If blocked, render children but with overlay to prevent interaction LC 09/03/2025
  if (currentAccessType === 'block') {
    return (
      <>
        <div
          style={{
            filter: 'blur(3px)',
            pointerEvents: 'none',
            userSelect: 'none',
            position: 'relative',
            opacity: 0.5,
          }}
        >
          {children}
        </div>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
        <FeatureBlockedModal
          isOpen={isFeatureBlockedModalOpen}
          onClose={handleModalClose}
          searchType={searchType}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
        />
      </>
    );
  }

  // If hidden, return null (will redirect in useEffect) LC 09/03/2025
  if (currentAccessType === 'hide') {
    return null;
  }

  // If 'view' or no feature key: render normally LC 09/03/2025
  return children;
}
